import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
const DURUM = { taslak: "Taslak (Google'a gönderilmedi)", duraklatilmis_gonderildi: "Google Ads'te duraklatılmış", yayinda: "Yayında" };

const confirmWord = (msg) => {
  const v = window.prompt(`${msg}\n\nOnaylamak için ONAYLIYORUM yazın:`);
  return v && v.trim().toUpperCase() === "ONAYLIYORUM" ? v : null;
};

function Req({ ok, children }) {
  return <li className="flex gap-2 items-start">{ok ? <CheckCircle2 className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}<span>{children}</span></li>;
}

function Setup({ st, reload, onAccounts }) {
  const [tok, setTok] = useState("");
  const [mcc, setMcc] = useState(st.ads_login_customer_id || "");
  const [cid, setCid] = useState(st.ads_customer_id || "");
  const [accounts, setAccounts] = useState([]);
  const save = () => api.put("/admin/ads/config", { developer_token: tok, login_customer_id: mcc, customer_id: cid })
    .then(() => { toast.success("Kaydedildi"); setTok(""); reload(); }).catch(err);
  const list = () => api.get("/admin/ads/accounts").then((r) => { setAccounts(r.data.items); onAccounts?.(); }).catch(err);
  return (
    <section className="bg-white border rounded-xl p-5 mb-4 text-sm" data-testid="ads-setup">
      <h2 className="font-head font-semibold text-slate-900 mb-3">Bağlantı gereksinimleri</h2>
      <ul className="space-y-2 text-xs mb-4" data-testid="ads-requirements">
        <Req ok={st.oauth_yapilandirildi}>Google Cloud OAuth bilgileri ve Google Ads API etkin — <Link to="/yonetim/search-console" className="text-teal-700 underline">Search Console sihirbazı 1. adım</Link></Req>
        <Req ok={st.bagli && st.izinler?.adwords}>Google hesabı “Google Ads” izniyle bağlı{st.bagli && !st.izinler?.adwords ? " — izin verilmemiş, hesabı yeniden bağlayın" : ""}</Req>
        <Req ok={st.ads_developer_token_var}>Developer Token: Google Ads yönetici (MCC) hesabında “Araçlar → API Merkezi”nden başvurun. Onay Google tarafından verilir; onay gelene kadar yalnızca test hesaplarıyla çalışır.</Req>
        <Req ok={!!st.ads_customer_id}>Reklam hesabı (müşteri numarası) seçili</Req>
      </ul>
      <div className="grid sm:grid-cols-3 gap-2">
        <Input type="password" placeholder={st.ads_developer_token_var ? "Token kayıtlı (değiştirmek için yazın)" : "Developer Token"} value={tok} onChange={(e) => setTok(e.target.value)} data-testid="ads-dev-token" />
        <Input placeholder="Yönetici hesap no (isteğe bağlı)" value={mcc} onChange={(e) => setMcc(e.target.value)} data-testid="ads-mcc" />
        <Input placeholder="Reklam hesabı no (XXX-XXX-XXXX)" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="ads-cid" />
      </div>
      <div className="flex gap-2 mt-2">
        <Button onClick={save} className="bg-teal-600 hover:bg-teal-700" data-testid="ads-save-config">Kaydet</Button>
        <Button variant="outline" onClick={list} disabled={!st.bagli || !st.ads_developer_token_var} data-testid="ads-list-accounts">Erişilebilen hesapları listele</Button>
      </div>
      {accounts.length > 0 && <p className="text-xs mt-2">Hesaplar: {accounts.map((a) => <button key={a} className="text-teal-700 underline mr-2" onClick={() => setCid(a)}>{a}</button>)}</p>}
    </section>
  );
}

function Report({ ready }) {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/ads/report").then((r) => setD(r.data)).catch(err);
  useEffect(() => { if (ready) load(); }, [ready]);
  if (!ready) return null;
  const total = (d?.items || []).reduce((a, x) => a + x.harcama, 0);
  return (
    <section className="bg-white border rounded-xl p-5 mb-4" data-testid="ads-report">
      <div className="flex items-center gap-2 mb-3"><h2 className="font-head font-semibold text-slate-900">Kampanya performansı ({d?.donem})</h2>
        <span className="text-xs text-slate-500 ml-auto">Son güncelleme: {d ? new Date(d.guncellendi).toLocaleString("tr-TR") : "—"}</span>
        <Button size="sm" variant="outline" onClick={load}>Yenile</Button></div>
      <p className="text-sm mb-3" data-testid="ads-spend">Reklam harcaması (Google Ads): <b>{total.toFixed(2)} {d?.items?.[0]?.para_birimi || ""}</b></p>
      <div className="overflow-x-auto"><table className="w-full text-xs"><thead><tr className="text-left text-slate-500"><th>Kampanya</th><th>Durum</th><th>Günlük bütçe</th><th>Tıklama</th><th>Gösterim</th><th>Harcama</th><th>Dönüşüm</th></tr></thead>
        <tbody>{(d?.items || []).map((c) => <tr key={c.id} className="border-t"><td className="py-1">{c.ad}</td><td>{c.durum}</td><td>{c.gunluk_butce}</td><td>{c.tiklama}</td><td>{c.gosterim}</td><td>{c.harcama.toFixed(2)}</td><td>{c.donusum}</td></tr>)}</tbody></table>
        {!d?.items?.length && <p className="text-xs text-slate-400 py-2">Bu dönemde kampanya verisi yok.</p>}</div>
    </section>
  );
}

const EMPTY = { ad: "", hedef_tur: "urun", hedef_id: "", final_url: "", gunluk_butce: 100, max_tbm: 5, basliklar: "", aciklamalar: "", anahtar_kelimeler: "" };

function DraftForm({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [hedef, setHedef] = useState("");
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const suggest = async () => {
    try {
      let id = hedef.trim();
      if (f.hedef_tur === "urun" && !id.startsWith("MLR")) {
        const r = await api.get(`/catalog/products/${encodeURIComponent(id)}`);
        id = r.data.product.urun_id;
      }
      const r = await api.get(`/admin/ads/suggest?tur=${f.hedef_tur}&id=${encodeURIComponent(id)}`);
      setF((x) => ({ ...x, hedef_id: id, ad: r.data.ad, final_url: r.data.final_url, basliklar: r.data.basliklar.join("\n"),
        aciklamalar: r.data.aciklamalar.join("\n"), anahtar_kelimeler: r.data.anahtar_kelimeler.join("\n") }));
    } catch (e) { err(e); }
  };
  const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const create = () => api.post("/admin/ads/drafts", { ...f, gunluk_butce: +f.gunluk_butce, max_tbm: +f.max_tbm,
    basliklar: lines(f.basliklar), aciklamalar: lines(f.aciklamalar), anahtar_kelimeler: lines(f.anahtar_kelimeler) })
    .then(() => { toast.success("Taslak kaydedildi"); setF(EMPTY); onCreated(); }).catch(err);
  const long = (s, n) => lines(s).filter((x) => x.length > n).length;
  return (
    <section className="bg-white border rounded-xl p-5 mb-4 text-sm space-y-2" data-testid="ads-draft-form">
      <h2 className="font-head font-semibold text-slate-900">Kampanya taslağı</h2>
      <p className="text-xs text-slate-500">Taslaklar yalnızca bu panelde saklanır; Google Ads'e siz onaylayana kadar gönderilmez. Öneriler katalog verisinden oluşturulur (yapay zekâ maliyeti yoktur).</p>
      <div className="flex flex-wrap gap-2">
        <select value={f.hedef_tur} onChange={(e) => set("hedef_tur", e.target.value)} className="border rounded-md px-2" data-testid="ads-target-type">
          <option value="urun">Ürün</option><option value="kategori">Kategori</option></select>
        <Input className="max-w-sm" placeholder={f.hedef_tur === "urun" ? "Ürün adresi (slug) veya ürün kimliği" : "Kategori kimliği (KAT-...)"} value={hedef} onChange={(e) => setHedef(e.target.value)} data-testid="ads-target-id" />
        <Button variant="outline" onClick={suggest} disabled={!hedef} data-testid="ads-suggest">Metin öner</Button>
      </div>
      <Input placeholder="Kampanya adı" value={f.ad} onChange={(e) => set("ad", e.target.value)} data-testid="ads-name" />
      <Input placeholder="Hedef sayfa adresi" value={f.final_url} onChange={(e) => set("final_url", e.target.value)} data-testid="ads-final-url" />
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">Günlük bütçe (hesap para birimi)<Input type="number" min="1" value={f.gunluk_butce} onChange={(e) => set("gunluk_butce", e.target.value)} data-testid="ads-budget" /></label>
        <label className="text-xs">En yüksek tıklama başı teklif<Input type="number" min="0.1" step="0.1" value={f.max_tbm} onChange={(e) => set("max_tbm", e.target.value)} data-testid="ads-cpc" /></label>
      </div>
      <label className="text-xs block">Başlıklar (her satır bir başlık, 3–15 adet, en fazla 30 karakter){long(f.basliklar, 30) > 0 && <span className="text-red-600"> · {long(f.basliklar, 30)} başlık uzun</span>}
        <Textarea value={f.basliklar} onChange={(e) => set("basliklar", e.target.value)} data-testid="ads-headlines" /></label>
      <label className="text-xs block">Açıklamalar (2–4 adet, en fazla 90 karakter){long(f.aciklamalar, 90) > 0 && <span className="text-red-600"> · {long(f.aciklamalar, 90)} açıklama uzun</span>}
        <Textarea value={f.aciklamalar} onChange={(e) => set("aciklamalar", e.target.value)} data-testid="ads-descriptions" /></label>
      <label className="text-xs block">Anahtar kelimeler (her satır bir kelime öbeği)
        <Textarea value={f.anahtar_kelimeler} onChange={(e) => set("anahtar_kelimeler", e.target.value)} data-testid="ads-keywords" /></label>
      <Button onClick={create} className="bg-teal-600 hover:bg-teal-700" data-testid="ads-save-draft">Taslağı kaydet</Button>
    </section>
  );
}

function Drafts({ items, reload, ready }) {
  const act = async (fn) => { try { await fn(); toast.success("İşlem tamamlandı"); reload(); } catch (e) { err(e); } };
  const publish = (d) => { const w = confirmWord(`“${d.ad}” Google Ads'e DURAKLATILMIŞ olarak gönderilecek (günlük bütçe ${d.gunluk_butce}). Reklam yayınlanmaz.`);
    if (w) act(() => api.post(`/admin/ads/drafts/${d.taslak_id}/publish`, { onay_metni: w })); };
  const status = (d, durum) => { const w = confirmWord(durum === "ENABLED" ? `“${d.ad}” kampanyası BAŞLATILACAK ve reklam harcaması oluşacak.` : `“${d.ad}” duraklatılacak.`);
    if (w) act(() => api.post(`/admin/ads/drafts/${d.taslak_id}/status`, { onay_metni: w, durum })); };
  const budget = (d) => { const v = window.prompt("Yeni günlük bütçe:", d.gunluk_butce); if (!v) return;
    const w = confirmWord(`Günlük bütçe ${d.gunluk_butce} → ${v} olarak değiştirilecek.`);
    if (w) act(() => api.post(`/admin/ads/drafts/${d.taslak_id}/budget`, { onay_metni: w, gunluk_butce: +v })); };
  return (
    <section className="space-y-3" data-testid="ads-drafts">
      {items.map((d) => (
        <div key={d.taslak_id} className="bg-white border rounded-xl p-4 text-xs" data-testid={`ads-draft-${d.taslak_id}`}>
          <div className="flex flex-wrap gap-2 items-center"><b className="text-sm">{d.ad}</b><Badge variant="secondary">{DURUM[d.durum]}</Badge>
            <span className="text-slate-500">Günlük bütçe: {d.gunluk_butce}</span></div>
          <p className="mt-1 break-all text-slate-500">{d.final_url}</p>
          <p className="mt-1">{d.basliklar.join(" | ")}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {d.durum === "taslak" && <><Button size="sm" disabled={!ready} onClick={() => publish(d)} data-testid={`ads-publish-${d.taslak_id}`}>Google Ads'e gönder (duraklatılmış)</Button>
              <Button size="sm" variant="outline" onClick={() => act(() => api.delete(`/admin/ads/drafts/${d.taslak_id}`))}>Sil</Button></>}
            {d.durum === "duraklatilmis_gonderildi" && <Button size="sm" className="bg-teal-600" onClick={() => status(d, "ENABLED")} data-testid={`ads-start-${d.taslak_id}`}>Kampanyayı başlat</Button>}
            {d.durum === "yayinda" && <Button size="sm" variant="outline" onClick={() => status(d, "PAUSED")}>Duraklat</Button>}
            {d.durum !== "taslak" && <Button size="sm" variant="outline" onClick={() => budget(d)}>Bütçeyi değiştir</Button>}
          </div>
        </div>
      ))}
      {!items.length && <p className="text-xs text-slate-400">Henüz taslak yok.</p>}
    </section>
  );
}

export default function AdminAds() {
  const [st, setSt] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [usage, setUsage] = useState(null);
  const load = () => api.get("/admin/google/status").then((r) => setSt(r.data));
  const loadDrafts = () => api.get("/admin/ads/drafts").then((r) => setDrafts(r.data.items));
  useEffect(() => { load(); loadDrafts(); api.get("/admin/seo/overview").then((r) => setUsage(r.data.ai_kullanim)); }, []);
  if (!st) return <p className="text-slate-400">Yükleniyor...</p>;
  const ready = st.bagli && st.izinler?.adwords && st.ads_developer_token_var && !!st.ads_customer_id;
  return (
    <div className="max-w-4xl" data-testid="admin-ads-page">
      <h1 className="font-head text-2xl font-bold text-slate-900 mb-1">Google Ads</h1>
      <p className="text-sm text-slate-500 mb-4">Onayınız olmadan reklam yayınlanmaz, kampanya başlatılmaz ve bütçe değiştirilmez. Gönderilen kampanyalar Google Ads'te duraklatılmış olarak oluşturulur.</p>
      <div className={`rounded-lg p-3 text-sm mb-4 border ${ready ? "bg-teal-50 border-teal-200" : "bg-slate-100"}`} data-testid="ads-connection-status">
        {ready ? "Google Ads bağlantısı hazır." : "Google Ads bağlantısı hazır değil — aşağıdaki eksik adımları tamamlayın. Taslak hazırlayabilirsiniz, ancak gönderim ve raporlar bağlantı tamamlanınca açılır."}
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-4 text-xs">
        <div className="bg-white border rounded-xl p-3" data-testid="ads-cost-box">Reklam harcaması: Google Ads hesabınızdan faturalandırılır; rapor bölümünde gösterilir.</div>
        <div className="bg-white border rounded-xl p-3" data-testid="ads-ai-cost-box">Platform/yapay zekâ kullanımı (SEO): bu ay {usage?.kullanilan ?? 0} / {usage?.aylik_sinir ?? "—"} kayıt. Reklam harcamasından ayrıdır.</div>
      </div>
      <Setup st={st} reload={load} />
      <Report ready={ready} />
      <DraftForm onCreated={loadDrafts} />
      <Drafts items={drafts} reload={loadDrafts} ready={ready} />
    </div>
  );
}
